import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { equals } from 'ramda';

import useSearchQuery from '@shared/v2/hooks/useSearchQuery'; // SearchQueryParam,

import { getSearchQuery } from '@shared/v2/hooks/useSearchQuery/utils';
import useToggle from '@shared/v2/hooks/useToggle';

import usePropertyContent from '@/components/PackagePage/hooks/usePropertyContent';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { UI } from '@/v2/types';
import { otherPackageModalDataLayerEvent } from '../../utils/dataLayer/dataLayer';
import { useSearchPanelControls } from '../SearchPanelControlsContext';
import {
  offerPrice,
  OtherPackageOffer,
  sortedCheapestOffersPerPackageOption,
} from './utils';

type SearchQueryParam = ReturnType<typeof getSearchQuery>;

export type OtherPackageModalContextValue = {
  setOn: (lastOpenedQuery?: SearchQueryParam) => void;
  setOff: () => void;
  isModalOpen: boolean;
  lastOpenedQuery?: SearchQueryParam;
  selectedOtherRoomOption?: UI.RoomOption;
  setSelectedOtherRoomOption: (roomOption?: UI.RoomOption) => void;
  didModalAutoOpen: boolean;
  setDidModalAutoOpen: (didAutoOpen: boolean) => void;
};
const OtherPackageModalContext = createContext<OtherPackageModalContextValue>({
  setOn: () => {},
  setOff: () => {},
  isModalOpen: false,
  lastOpenedQuery: undefined,
  selectedOtherRoomOption: undefined,
  setSelectedOtherRoomOption: () => {},
  didModalAutoOpen: false,
  setDidModalAutoOpen: () => {},
});

const OtherPackageModalProvider = ({ children }: { children: ReactNode }) => {
  const { setOn, setOff, value: isModalOpen } = useToggle(false);
  const [didModalAutoOpen, setDidModalAutoOpen] = useState(false);
  const [lastOpenedQuery, setLastOpenedQuery] = useState<SearchQueryParam>();
  const [selectedOtherRoomOption, setSelectedOtherRoomOption] = useState<
    UI.RoomOption | undefined
  >();

  const handleModalOpen = (queryParams?: SearchQueryParam) => {
    if (queryParams) {
      setLastOpenedQuery(queryParams);
    }

    setOn();
  };

  return (
    <OtherPackageModalContext.Provider
      value={{
        setOn: handleModalOpen,
        setOff,
        isModalOpen,
        lastOpenedQuery,
        selectedOtherRoomOption,
        setSelectedOtherRoomOption,
        didModalAutoOpen,
        setDidModalAutoOpen,
      }}
    >
      {children}
    </OtherPackageModalContext.Provider>
  );
};

/*
  The `shouldAutoOpen` flag exists so that we can pass it in once, (in the `OtherPackageModal`).
  This prevents this hook from attempting to automatically opening everywhere it's invoked.
*/
const useOtherPackageModal = ({
  shouldAutoOpen,
}: {
  shouldAutoOpen?: boolean;
} = {}) => {
  const { searchQuery, isReady: isSearchQueryReady } = useSearchQuery();
  const { isMobileSearchPanelModalOpen } = useSearchPanelControls();

  const {
    route,
    packageOptions,
    isPreSearch,
    isLoading,
    selectedPackageOption,
    roomOptions,
    selectedRoomOption,
  } = useRoomOptionsData() || {};
  const propertyContent = usePropertyContent() || {};

  const {
    setOff,
    setOn,
    isModalOpen,
    selectedOtherRoomOption,
    setSelectedOtherRoomOption,
    lastOpenedQuery,
    setDidModalAutoOpen,
    didModalAutoOpen,
  } = useContext(OtherPackageModalContext);

  const validOffers = useMemo(() => {
    /*
     *   selectedOtherRoomOption: {
     *     leadInOffers: []      Pre AND post-search, this is Sanity leadInOffers (Procured Offers).
     *     offers: [{            Pre-search, offers WILL BE undefined
     *       charges: {}         Post-search pricing.
     *       leadInOffer: {}     Post-search leadInOffer (Procured Offer).
     *                           - If this is undefined:
     *                             - And selectedOtherRoomOption.leadInOffers is not empty, it means this room is available on
     *                               a Procured Offer package, but not for the selected dates.
     *                             - And selectedOtherRoomOption.leadInOffers is empty, it means that this room is only available
     *                               as a Standard Rate. The user must select Best Available Rate to see this room.
     *     }]
     *   }
     */
    if (!route || !packageOptions) return;

    if (isPreSearch) {
      const preSearchOffers = (selectedOtherRoomOption?.leadInOffers ?? [])
        ?.filter((l) => !!l?.packageOffers?.[route]?.total?.amount)
        .sort((a, b) => {
          // TODO: Make cleaner
          const aOffer = offerPrice({ charges: a?.packageOffers?.[route] });
          const bOffer = offerPrice({ charges: b?.packageOffers?.[route] });
          return aOffer - bOffer;
        })
        .map((leadInOffer) => ({
          leadInOffer,
          offer: undefined, // This is the post-search offer
          packageOption: packageOptions.find(
            (po) => po.id === leadInOffer.inclusionSetId,
          ),
        }));

      return preSearchOffers;
    } else if (selectedOtherRoomOption) {
      return (selectedOtherRoomOption.offers ?? []).map((offer) => ({
        leadInOffer: offer.leadInOffer,
        offer,
        packageOption:
          offer.leadInOffer &&
          packageOptions.find(
            (po) => po.id === offer.leadInOffer!.inclusionSetId,
          ),
      }));
    }

    // Post-Search, where NO room has been selected.
    return sortedCheapestOffersPerPackageOption({
      roomOptions: roomOptions?.all,
      packageOptions,
    });
  }, [
    isPreSearch,
    packageOptions,
    route,
    selectedOtherRoomOption,
    roomOptions?.all,
  ]);

  /* We need to Automatically open this modal (with the "Alternative" variant, IF:
    - Modal is not already open
    - Mobile search panel is not open
    - Is Post Search
    - NO room has been selected
    - There is at least 1 "Main room" AND
      - None of those rooms have a matching offer (eg. there are no prices post-search)
    - Modal has not just opened up with these exact search params
      - We need to to add the current search params,
        because we are programatically opening the modal and we need to do a diff to ensure
        that is doesn't constantly open on re-renders, BUT does re-open on subsequent searches.
  */
  useEffect(() => {
    if (
      shouldAutoOpen &&
      !isModalOpen &&
      !isPreSearch &&
      !isLoading &&
      !isMobileSearchPanelModalOpen &&
      isSearchQueryReady
    ) {
      if (
        !!selectedOtherRoomOption ||
        !!selectedRoomOption ||
        !!searchQuery?.room
      ) {
        return;
      }

      if (!roomOptions?.main || roomOptions.main.length < 1) return;

      const hasMatchingOffer = roomOptions?.main?.some(
        (r) => !!r.matchingOffer,
      );

      const hasAlreadyOpened = equals(searchQuery, lastOpenedQuery);

      if (hasMatchingOffer || hasAlreadyOpened) return;

      otherPackageModalDataLayerEvent({
        variant: 'change_package_options_modal',
        action: 'open',
        propertyContent,
        searchQuery,
      });

      setDidModalAutoOpen(true);
      setOn(searchQuery);
    }
  }, [
    isLoading,
    isPreSearch,
    isSearchQueryReady,
    isModalOpen,
    roomOptions?.main,
    lastOpenedQuery,
    searchQuery,
    selectedRoomOption,
    selectedOtherRoomOption,
    validOffers,
    shouldAutoOpen,
    isMobileSearchPanelModalOpen,
  ]);

  const handleOpen = (roomOption: UI.RoomOption, ctaText?: string) => {
    otherPackageModalDataLayerEvent({
      variant: 'room_package_options_modal',
      action: 'open',
      propertyContent,
      searchQuery,
      roomName: roomOption.roomType?.name,
      ctaText,
    });

    setSelectedOtherRoomOption(roomOption);
    setDidModalAutoOpen(false);

    /*
      We want to store the current searchQuery that the user opens the modal with,
      as we have logic that will _automatically_ open this modal if certain criteria are met.
      Storing the searchQuery that exists as the user opens this modal means that we will not
      also programatically trigger the automatic modal after a user opens the modal manually.
    */
    setOn(searchQuery);
  };

  const handleClose = (roomId?: string) => {
    if (!roomId) {
      // GA event for when a user closes the modal without selecting a room
      otherPackageModalDataLayerEvent({
        variant: didModalAutoOpen
          ? 'change_package_options_modal'
          : 'room_package_options_modal',
        action: 'close',
        propertyContent,
        searchQuery,
        roomId: roomId || selectedOtherRoomOption?.roomType?.id,
      });
    }

    if (!roomId && selectedOtherRoomOption) {
      setSelectedOtherRoomOption(undefined);
    }

    setOff();
  };

  return {
    openOtherPackageModal: handleOpen,
    closeOtherPackageModal: handleClose,
    isOtherPackageModalOpen: isModalOpen,
    selectedOtherRoomOption,
    selectedPackageOption,
    offers: validOffers as OtherPackageOffer[],
    route,
    isReady: !isLoading && validOffers !== undefined,
    lastOpenedQuery,
    roomOptions,
    didModalAutoOpen,
  };
};

export { OtherPackageModalProvider, useOtherPackageModal };
